import {useEffect} from 'react';
import type {Component, Editor} from 'grapesjs';
import {useAppDispatch} from '@/hooks';
import {closeModal} from '@Components/modal-container/modal-container-reducer';

export interface UseCloseSheetOnCanvasInteractionOptions {
  /** Also close on `component:add` — the block-sheet dismisses once its block lands on the canvas. */
  closeOnAdd?: boolean;
  /** Keep the sheet open when this component's cid is re-selected (mobile-control-sheet re-tap). */
  skipSelectedCid?: string;
}

/**
 * Closes a bottom-sheet modal when the user interacts with the GrapesJS canvas. The canvas is an
 * iframe, so outside taps arrive as component:selected/deselected events rather than DOM clicks.
 */
export function useCloseSheetOnCanvasInteraction(editor: Editor | null | undefined, modalId: string, options: UseCloseSheetOnCanvasInteractionOptions = {}): void {
  const dispatch = useAppDispatch();
  const {closeOnAdd = false, skipSelectedCid} = options;

  useEffect((): (() => void) | undefined => {
    if (!editor) {
      return undefined;
    }

    const close = (): void => {
      dispatch(closeModal(modalId));
    };

    const onSelected = (component: Component): void => {
      if (skipSelectedCid !== undefined && component.cid === skipSelectedCid) {
        return;
      }
      close();
    };

    if (closeOnAdd) {
      editor.on('component:add', close);
    }
    editor.on('component:selected', onSelected);
    editor.on('component:deselected', close);

    return (): void => {
      if (closeOnAdd) {
        editor.off('component:add', close);
      }
      editor.off('component:selected', onSelected);
      editor.off('component:deselected', close);
    };
  }, [editor, dispatch, modalId, closeOnAdd, skipSelectedCid]);
}
